import { useEffect, useMemo, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { api } from "@/lib/api";
import { useUser } from "@/store/user";
import AIChat from "@/components/AIChat";
import EvaluationCard from "@/components/EvaluationCard";
import CompletionScreen from "@/components/CompletionScreen";
import Activity, { MCQActivity } from "@/components/Activities";
import FinalQuiz from "@/components/FinalQuiz";
import Paywall from "@/components/Paywall";
import VocabularyList from "@/components/VocabularyList";

/**
 * Render highlights inside an example text by wrapping matching phrases.
 */
function HighlightedText({ text, highlights }) {
  if (!highlights?.length) return <span>{text}</span>;
  let parts = [text];
  highlights.forEach(({ phrase, note }) => {
    parts = parts.flatMap((p) => {
      if (typeof p !== "string") return [p];
      const idx = p.indexOf(phrase);
      if (idx === -1) return [p];
      return [
        p.slice(0, idx),
        <mark key={`${phrase}-${idx}`} className="example-mark" title={note}>
          {phrase}
        </mark>,
        p.slice(idx + phrase.length),
      ];
    });
  });
  return <>{parts}</>;
}

/* =========================================================================
 * Stage builder — picks stages based on which keys are present in lesson data
 * ========================================================================= */
function buildStages(lesson) {
  const stages = [];
  stages.push({ key: "intro", label: "المقدمة", icon: "🎯" });
  if (lesson.explanation_sections?.length || lesson.explanation) {
    stages.push({ key: "explanation", label: "الشرح", icon: "📖" });
  }
  if (lesson.vocabulary?.length) {
    stages.push({ key: "vocabulary", label: "المفردات", icon: "📚" });
  }
  if (lesson.examples?.length) {
    stages.push({ key: "examples", label: "الأمثلة", icon: "💡" });
  }
  if (lesson.activities?.length) {
    stages.push({ key: "activities", label: "أنشطة تفاعليّة", icon: "🎮" });
  }
  if (lesson.exercise) {
    stages.push({ key: "exercise", label: "تمرين موجَّه", icon: "✍️" });
  }
  if (lesson.conversation_prompt) {
    stages.push({ key: "conversation", label: "محادثة مع AI", icon: "💬" });
  }
  if (lesson.writing_prompt) {
    stages.push({ key: "writing", label: "مهمّة كتابة", icon: "📝" });
  }
  if (lesson.quiz?.questions?.length) {
    stages.push({ key: "quiz", label: "اختبار نهائي", icon: "🏁" });
  }
  if (lesson.summary) {
    stages.push({ key: "summary", label: "الملخّص", icon: "📌" });
  }
  return stages;
}

const NavBtns = ({
  onPrev,
  onNext,
  onFinish,
  nextLabel = "التالي ←",
  nextDisabled = false,
  nextTestId = "next-stage-btn",
  isLast = false,
}) => (
  <div className="row" style={{ justifyContent: "space-between", marginTop: 18 }}>
    {onPrev ? (
      <button className="btn btn-ghost" onClick={onPrev}>السابق</button>
    ) : <span />}
    {isLast ? (
      <button
        className="btn btn-gold"
        onClick={onFinish}
        disabled={nextDisabled}
        data-testid="complete-lesson-btn"
      >
        أكمل الدرس واحصل على الشارة 🏅
      </button>
    ) : (
      <button className="btn btn-primary" onClick={onNext} disabled={nextDisabled} data-testid={nextTestId}>
        {nextLabel}
      </button>
    )}
  </div>
);

export default function LessonPage() {
  const { lessonId } = useParams();
  const navigate = useNavigate();
  const { refresh } = useUser();

  const [lesson, setLesson] = useState(null);
  const [stageIdx, setStageIdx] = useState(0);
  const [showCompletion, setShowCompletion] = useState(false);
  const [earnedXp, setEarnedXp] = useState(0);
  const [usedRhetoric, setUsedRhetoric] = useState(false);
  const [loading, setLoading] = useState(true);
  const [quizScore, setQuizScore] = useState(null);
  const [activitiesCompleted, setActivitiesCompleted] = useState({});
  const [lockMessage, setLockMessage] = useState(null);
  const [paywall, setPaywall] = useState(null); // {track_id, message}
  const [loadError, setLoadError] = useState(null);

  useEffect(() => {
    setLoading(true);
    setStageIdx(0);
    setEarnedXp(0);
    setActivitiesCompleted({});
    setQuizScore(null);
    setLockMessage(null);
    setPaywall(null);
    setLoadError(null);
    setLesson(null);
    api.getLesson(Number(lessonId))
      .then(setLesson)
      .catch((err) => {
        const status = err?.response?.status;
        const detail = err?.response?.data?.detail;
        if (status === 402 && typeof detail === "object") {
          setPaywall({
            track_id: detail.track_id,
            product_id: detail.product_id,
            message: detail.message,
          });
        } else if (status === 423) {
          setLockMessage(detail || "هذا الدرس مقفل. أكمل الدرس السابق أوّلًا.");
        } else if (status === 404) {
          setLoadError("لم نعثر على هذا الدرس.");
        } else {
          setLoadError((typeof detail === "string" ? detail : null) || "تعذّر تحميل الدرس. حاول مرّةً أخرى.");
        }
      })
      .finally(() => setLoading(false));
  }, [lessonId]);

  const stages = useMemo(() => (lesson ? buildStages(lesson) : []), [lesson]);
  const progressPct = stages.length
    ? Math.round(((stageIdx + 1) / stages.length) * 100)
    : 0;

  const goNext = () => setStageIdx((i) => Math.min(stages.length - 1, i + 1));
  const goPrev = () => setStageIdx((i) => Math.max(0, i - 1));

  const finishLesson = async () => {
    if (!lesson) return;
    let totalXp = earnedXp || lesson.xp.completion;
    if (quizScore !== null) {
      totalXp += Math.round((lesson.xp.completion * quizScore) / 100);
    }
    try {
      await api.completeLesson(lesson.id, {
        xp_earned: totalXp,
        used_rhetoric: usedRhetoric,
      });
      await refresh();
    } catch (e) {
      console.error(e);
    }
    setShowCompletion(true);
  };

  if (loading) {
    return (
      <div className="shell">
        <div className="muted" data-testid="lesson-loading">يُحمَّل الدرس...</div>
      </div>
    );
  }

  if (paywall) {
    return (
      <Paywall
        trackId={paywall.track_id}
        productId={paywall.product_id}
        message={paywall.message}
        onBack={() => navigate("/tracks")}
      />
    );
  }

  if (lockMessage) {
    return (
      <div className="shell" data-testid="lesson-locked-screen">
        <div className="back-link" onClick={() => navigate("/tracks")} data-testid="locked-back-to-tracks">
          ← العودة إلى المسارات
        </div>
        <div className="panel" style={{ textAlign: "center", padding: "40px 24px", marginTop: 24 }}>
          <div style={{ fontSize: 52, marginBottom: 12 }}>🔒</div>
          <h2 style={{ marginBottom: 12 }}>الدرس مقفل</h2>
          <p className="muted" style={{ maxWidth: 480, margin: "0 auto 20px", lineHeight: 1.7 }}>
            {lockMessage}
          </p>
          <button
            className="btn btn-primary"
            onClick={() => navigate("/tracks")}
            data-testid="go-to-tracks-btn"
          >
            استكشف المسارات
          </button>
        </div>
      </div>
    );
  }

  if (loadError || !lesson) {
    return (
      <div className="shell" data-testid="lesson-error-screen">
        <div className="back-link" onClick={() => navigate("/tracks")} data-testid="error-back-to-tracks">
          ← العودة إلى المسارات
        </div>
        <div className="panel" style={{ textAlign: "center", padding: "40px 24px", marginTop: 24 }}>
          <div style={{ fontSize: 52, marginBottom: 12 }}>⚠️</div>
          <h2 style={{ marginBottom: 12 }}>تعذّر فتح الدرس</h2>
          <p className="muted" style={{ maxWidth: 480, margin: "0 auto 20px", lineHeight: 1.7 }}>
            {loadError || "لم نتمكّن من تحميل هذا الدرس."}
          </p>
          <button
            className="btn btn-primary"
            onClick={() => navigate("/tracks")}
            data-testid="go-to-tracks-btn"
          >
            العودة إلى المسارات
          </button>
        </div>
      </div>
    );
  }

  const stage = stages[stageIdx]?.key;
  const isLastStage = stageIdx === stages.length - 1;

  return (
    <div className="shell" data-testid={`lesson-page-${lesson.id}`}>
      <div className="back-link" onClick={() => navigate("/")} data-testid="back-to-home">
        ← العودة إلى الدروس
      </div>

      {/* Header panel */}
      <div className="panel" style={{ marginBottom: 18 }}>
        <div className="row" style={{ justifyContent: "space-between", marginBottom: 10 }}>
          <div>
            <div className="muted" style={{ fontSize: 13 }}>الدرس {lesson.id}</div>
            <h1 style={{ margin: "4px 0", fontFamily: "'Amiri', serif", fontSize: 32 }}>
              {lesson.title}
            </h1>
            <div className="meta muted" style={{ display: "flex", gap: 10, fontSize: 13 }}>
              <span>⏱ {lesson.duration}</span>
              <span>·</span>
              <span>{lesson.level}</span>
              <span>·</span>
              <span>{lesson.skill}</span>
            </div>
          </div>
          <div className="badge-pill">
            <span style={{ fontSize: 18 }}>{lesson.badge.icon}</span>
            {lesson.badge.name}
          </div>
        </div>

        <div className="muted" style={{ fontSize: 12, marginBottom: 6 }}>
          {stages[stageIdx]?.label} · {progressPct}%
        </div>
        <div className="progress-track" data-testid="lesson-progress">
          <div className="progress-fill" style={{ width: `${progressPct}%` }} />
        </div>

        <div className="stepper" style={{ marginTop: 14 }} data-testid="lesson-stepper">
          {stages.map((s, i) => (
            <span
              key={s.key}
              className={`pill ${i === stageIdx ? "active" : ""} ${i < stageIdx ? "done" : ""}`}
              onClick={() => setStageIdx(i)}
              data-testid={`stage-pill-${s.key}`}
            >
              {i < stageIdx ? "✓" : s.icon} {s.label}
            </span>
          ))}
        </div>
      </div>

      {/* Stage 1: Intro */}
      {stage === "intro" && (
        <div className="panel" data-testid="stage-intro">
          <h2><span className="step-num">1</span> مقدمة الدرس</h2>
          <p style={{ fontSize: 17, lineHeight: 1.9, color: "var(--text-dim)" }}>{lesson.objective}</p>

          {lesson.learning_objectives?.length > 0 && (
            <>
              <h3 style={{ marginTop: 22, fontSize: 18 }}>🎯 أهداف التعلّم</h3>
              <ul className="lo-list" data-testid="learning-objectives">
                {lesson.learning_objectives.map((o, i) => (
                  <li key={i}><span className="lo-dot" /> {o}</li>
                ))}
              </ul>
            </>
          )}

          {lesson.scenario && (
            <div className="scenario-box" data-testid="intro-scenario">
              <div className="scenario-tag">السيناريو</div>
              <div>{lesson.scenario}</div>
            </div>
          )}

          <div className="row" style={{ marginTop: 22, gap: 10 }}>
            <span className="badge-pill">⏱ {lesson.duration}</span>
            <span className="badge-pill">+{lesson.xp.completion + lesson.xp.rhetorical_bonus} XP</span>
          </div>

          <NavBtns
            onPrev={null}
            onNext={goNext}
            onFinish={finishLesson}
            isLast={isLastStage}
            nextLabel="ابدأ الدرس ←"
            nextTestId="start-lesson-btn"
          />
        </div>
      )}

      {/* Stage 2: Explanation */}
      {stage === "explanation" && (
        <div className="panel" data-testid="stage-explanation">
          <h2><span className="step-num">2</span> الشرح</h2>
          {lesson.explanation_sections?.length ? (
            lesson.explanation_sections.map((sec, i) => (
              <ExplanationSection key={i} section={sec} index={i} />
            ))
          ) : (
            <p style={{ fontSize: 17, lineHeight: 1.95 }}>{lesson.explanation}</p>
          )}
          <NavBtns onPrev={goPrev} onNext={goNext} onFinish={finishLesson} isLast={isLastStage} />
        </div>
      )}

      {/* Stage 3: Vocabulary */}
      {stage === "vocabulary" && (
        <div className="panel" data-testid="stage-vocabulary">
          <h2><span className="step-num">3</span> المفردات والأدوات</h2>
          <p className="dim">استخدم هذه المفردات لتبني أسلوبًا أكثر بلاغة:</p>
          <VocabularyList items={lesson.vocabulary} />
          <NavBtns onPrev={goPrev} onNext={goNext} onFinish={finishLesson} isLast={isLastStage} />
        </div>
      )}

      {/* Stage 4: Examples */}
      {stage === "examples" && (
        <div className="panel" data-testid="stage-examples">
          <h2><span className="step-num">4</span> أمثلة تفاعليّة</h2>
          <p className="dim" style={{ marginBottom: 6 }}>مرّر فوق العناصر المُمَيَّزة لمعرفة دلالتها.</p>
          {lesson.examples.map((ex, i) => {
            const cls = ex.type.includes("بلاغ") || ex.type.includes("كنائي")
              ? "rhetorical"
              : ex.type.includes("أقوى") ? "strong" : "normal";
            return (
              <div key={i} className={`example ${cls}`} data-testid={`example-${i}`}>
                <div className="tag">{ex.type}</div>
                <div className="text">
                  <HighlightedText text={ex.text} highlights={ex.highlights} />
                </div>
              </div>
            );
          })}
          <NavBtns onPrev={goPrev} onNext={goNext} onFinish={finishLesson} isLast={isLastStage} />
        </div>
      )}

      {/* Stage 5: Activities */}
      {stage === "activities" && (
        <div className="panel" data-testid="stage-activities">
          <h2><span className="step-num">5</span> أنشطة تفاعليّة</h2>
          <p className="dim" style={{ marginBottom: 16 }}>
            اختبر فهمك من خلال هذه التمارين القصيرة. كلّ نشاط بنوع مختلف.
          </p>
          {lesson.activities.map((act, i) => (
            <div key={i} className="activity-wrap" data-testid={`activity-${i}`}>
              <div className="activity-num">النشاط {i + 1}</div>
              <Activity
                activity={act}
                onAnswer={(ok) => {
                  setActivitiesCompleted((prev) => ({ ...prev, [i]: ok }));
                  if (ok) setEarnedXp((x) => x + 5);
                }}
              />
            </div>
          ))}
          <NavBtns onPrev={goPrev} onNext={goNext} onFinish={finishLesson} isLast={isLastStage} />
        </div>
      )}

      {/* Stage 6: Guided Exercise */}
      {stage === "exercise" && (
        <div className="panel" data-testid="stage-exercise">
          <h2><span className="step-num">6</span> تمرين موجَّه</h2>
          <div style={{ lineHeight: 1.9, fontSize: 16 }}>
            <strong>{lesson.exercise.question}</strong>
            <div style={{ marginTop: 6 }} className="dim">«{lesson.exercise.prompt}»</div>
            <div className="hint">💡 {lesson.exercise.hint}</div>
          </div>
          <div className="spacer" />
          <EvaluationCard
            lesson={lesson}
            activity="exercise"
            defaultPlaceholder="اكتب إجابتك هنا بأسلوبك..."
            onScored={(r) => {
              setEarnedXp((x) => x + r.xp_earned);
              if (r.used_rhetoric) setUsedRhetoric(true);
            }}
          />
          <NavBtns onPrev={goPrev} onNext={goNext} onFinish={finishLesson} isLast={isLastStage} />
        </div>
      )}

      {/* Stage 7: AI Conversation */}
      {stage === "conversation" && (
        <div className="panel" data-testid="stage-conversation">
          <h2><span className="step-num">7</span> تدرّب مع AI</h2>
          <p className="dim">{lesson.conversation_prompt}</p>
          <AIChat lesson={lesson} prompt={lesson.conversation_prompt} />
          <NavBtns onPrev={goPrev} onNext={goNext} onFinish={finishLesson} isLast={isLastStage} nextLabel="التالي ←" />
        </div>
      )}

      {/* Stage 8: Writing */}
      {stage === "writing" && (
        <div className="panel" data-testid="stage-writing">
          <h2><span className="step-num">8</span> مهمّة الكتابة</h2>
          <p style={{ lineHeight: 1.9 }}>{lesson.writing_prompt}</p>
          <EvaluationCard
            lesson={lesson}
            activity="writing"
            defaultPlaceholder="اكتب فقرتك هنا..."
            onScored={(r) => {
              setEarnedXp((x) => x + r.xp_earned);
              if (r.used_rhetoric) setUsedRhetoric(true);
            }}
          />
          <NavBtns onPrev={goPrev} onNext={goNext} onFinish={finishLesson} isLast={isLastStage} />
        </div>
      )}

      {/* Stage 9: Final Quiz */}
      {stage === "quiz" && (
        <div className="panel" data-testid="stage-quiz">
          <h2><span className="step-num">9</span> الاختبار النهائي</h2>
          <p className="dim">أجب عن جميع الأسئلة. ستحصل على تصحيح فوريّ لكلّ سؤال.</p>
          <FinalQuiz
            questions={lesson.quiz.questions}
            passingScore={lesson.quiz.passing_score || 60}
            onFinished={({ score }) => {
              setQuizScore(score);
            }}
          />
          <NavBtns onPrev={goPrev} onNext={goNext} onFinish={finishLesson} isLast={isLastStage} nextLabel="إلى الملخّص ←" />
        </div>
      )}

      {/* Stage 10: Summary */}
      {stage === "summary" && (
        <div className="panel" data-testid="stage-summary">
          <h2><span className="step-num">10</span> ملخّص الدرس</h2>
          {lesson.summary?.key_points?.length > 0 && (
            <>
              <h3 style={{ fontSize: 18 }}>📌 أهمّ النقاط</h3>
              <ul className="lo-list" data-testid="summary-points">
                {lesson.summary.key_points.map((p, i) => (
                  <li key={i}><span className="lo-dot gold" /> {p}</li>
                ))}
              </ul>
            </>
          )}
          {lesson.summary?.review_card && (
            <div className="review-card" data-testid="review-card">
              <div className="review-tag">بطاقة المراجعة السريعة</div>
              <div className="review-front">{lesson.summary.review_card.front}</div>
              <div className="review-back">{lesson.summary.review_card.back}</div>
            </div>
          )}
          <div className="row" style={{ justifyContent: "space-between", marginTop: 22 }}>
            <button className="btn btn-ghost" onClick={goPrev}>السابق</button>
            <button
              className="btn btn-gold"
              onClick={finishLesson}
              data-testid="complete-lesson-btn"
            >
              أكمل الدرس واحصل على الشارة 🏅
            </button>
          </div>
        </div>
      )}

      {showCompletion && (
        <CompletionScreen
          lesson={lesson}
          xp={earnedXp || lesson.xp.completion}
          isLastLesson={false}
          onClose={() => { setShowCompletion(false); navigate("/tracks"); }}
          onContinue={() => {
            setShowCompletion(false);
            navigate("/tracks");
          }}
        />
      )}
    </div>
  );
}

/* ---------- Sub: an explanation section with optional inline check ---------- */
function ExplanationSection({ section, index }) {
  return (
    <div className="explain-section" data-testid={`explain-section-${index}`}>
      {section.visual && (
        <div className="explain-visual" aria-hidden>
          {section.visual}
        </div>
      )}
      <div className="explain-body">
        {section.heading && <h3 className="explain-heading">{section.heading}</h3>}
        <p className="explain-text">{section.text}</p>
        {section.inline_check && (
          <div className="inline-check" data-testid={`inline-check-${index}`}>
            <div className="muted" style={{ fontSize: 12, marginBottom: 6, fontWeight: 700 }}>
              تحقّق سريع ✓
            </div>
            <MCQActivity
              question={section.inline_check.question}
              options={section.inline_check.options}
              correctIndex={section.inline_check.correct_index}
              explanation={section.inline_check.explanation}
              compact
            />
          </div>
        )}
      </div>
    </div>
  );
}
